import { AccordionTabChangeEvent } from 'primereact/accordion';

/**
 * Interface for defining individual tab properties in the Accordion component.
 */
export interface IAccordionTab {
  /**
   * The text to display in the tab header.
   */
  header?: string | React.ReactNode;

  /**
   * The content to display within the tab body.
   */
  body: React.ReactNode;
}

export enum AccordionVariant {
  Default = 'default',
  Primary = 'primary',
  Secondary = 'secondary',
  Transparent = 'transparent',
}

export enum AccordionSize {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
}

export interface IAccordion {
  /**
   * An array of tabs to render. Each tab must include `header` and `body` properties.
   */
  tabs: IAccordionTab[];

  /**
   * Optional custom CSS class for the accordion container.
   */
  className?: string;

  /**
   * The variant of the accordion.
   */
  variant?: AccordionVariant;

  /**
   * The size of the accordion.
   */
  size?: AccordionSize;

  /**
   * Optional prefix for tab headers.
   */
  tabHeaderPrefix?: string;

  /**
   * The currently active tab index.
   * When null, all tabs are closed.
   */
  activeIndex?: number | number[] | null;

  /**
   * Allows multiple tabs to be open simultaneously if set to true.
   */
  multiple?: boolean;

  /**
   * Prevents deletion of the first tab if only one tab remains.
   */
  shouldPreserveFirstTab?: boolean;

  /**
   *  Allows the delete button to be displayed for each tab when set to true.
   */
  allowToDelete?: boolean;

  /**
   * Disables the delete button for all tabs when set to true.
   */
  isDeleteDisabled?: boolean;

  /**
   * Configuration for adding and managing additional tabs.
   * - `buttonLabel`: The label for the add button displayed in the accordion header.
   * - `onAdd`: Callback function triggered when the add button is clicked.
   * - `disableAdd`: Disables the add button when set to `true`.
   */
  additionalTabConfig?: {
    buttonLabel: string;
    onAdd: () => void;
    disableAdd?: boolean;
  };

  /**
   * Determines whether the active (open) accordion tab should be highlighted.
   * Defaults to `true`
   */
  highlightActive?: boolean;

  /**
   * Indicates whether the accordion should show in collapsed state.
   * Defaults to `false`.
   * If set to `true`, the accordion will be in collapsed state by default.
   */
  showCollapsed?: boolean;

  /**
   * Prevents all tabs from being toggled (expanded/collapsed).
   * Defaults to `false`.
   */
  preventTabsToggle?: boolean;

  /**
   * Callback triggered when a tab is deleted.
   * @param index - The index of the tab being deleted.
   */
  onDeleteTab?: (index: number) => void;

  /**
   * Callback triggered when a tab is opened or closed.
   * @param event - The event object containing details about the tab change.
   */
  onTabChange?: (event: AccordionTabChangeEvent) => void;
}
